import {Intent, Position, Toaster} from "@blueprintjs/core";
import {Job, Manifest, ManifestStop} from "../../generated/graphql";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import styled from "@emotion/styled";
import "./toaster.css";
import {navigateToCmsOrder} from "../../services/CmsService";
import React, {Dispatch, useCallback, useContext, useState} from "react";
import {AuthState} from "../../providers/AuthProvider";
import {LoadingSpinner} from "../../components/common/LoadingSpinner";
import {PreferenceContext} from "../../providers/PreferenceProvider";
import {v4 as uuidv4} from "uuid";
import {ManifestDetailsActions} from "../../providers/ManifestDetailsProvider";
import {COLORS} from "../../constants/constants";

export const AppToaster = Toaster.create({
  position: Position.TOP,
  maxToasts: 3
});

export const AppToasterTopLeft = Toaster.create({
  position: Position.TOP_LEFT,
  maxToasts: 3,
  className: "top-left-toast"
});

export const AssignmentToastContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const AssignmentHeader = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
`;

export const AssignmentTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
`;
const AssignmentBody = styled.div``;
const AssignmentFooter = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;
const AssignmentLink = styled.span`
  color: #137cbd;
  :hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;
const CloseButton = styled.button`
  padding: 6px 14px;
  background: #fff;
  border-radius: 4px;
  font-size: 14px;
  box-shadow: 0 2px 2px 0 #00000040;
  border: none;
  cursor: pointer;
`;
type ReassignmentToastProps = {
  jobStops: ManifestStop[];
  manifest: Manifest;
  authState: AuthState;
  authToken: string;
};

type AssignmentToastProps = {
  jobs: Job[];
  manifest: Manifest;
  authState: AuthState;
  selectedOrderId?: number;
  authToken: string;
};
const AssignmentToast = ({jobs, manifest, authState, selectedOrderId, authToken}: AssignmentToastProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const {tenantPreferences} = useContext(PreferenceContext);

  const handleJobClicked = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, job: Job) => {
      e.preventDefault();
      setLoading(true);
      navigateToCmsOrder(authState.tenant!, authToken, selectedOrderId ?? job.order.orderId, tenantPreferences).finally(
        () => setLoading(false)
      );
    },
    [authState.tenant, authToken, selectedOrderId, tenantPreferences]
  );

  return (
    <div>
      <LoadingSpinner loading={loading}>
        <AssignmentToastContainer>
          <AssignmentHeader>
            <FontAwesomeIcon size={"xl"} color={"#32A467"} icon={solid("circle-check")} />
            <AssignmentTitle>Assignment Submitted</AssignmentTitle>
          </AssignmentHeader>
          <div>
            Job{jobs.length > 1 ? "s" : ""}{" "}
            <span>
              {jobs.map((x, index) => {
                return (
                  <a key={`link-${x.jobId}`} href={"./"} onClick={(e) => handleJobClicked(e, x)}>
                    {x.jobNumber}
                    {index === jobs.length - 1 ? " " : ", "}
                  </a>
                );
              })}
            </span>{" "}
            submitted to {manifest.driver.name} and assignment is processing. We&apos;ll notify you of any issues.
          </div>
        </AssignmentToastContainer>
      </LoadingSpinner>
    </div>
  );
};

export const showAssignmentToast = (
  jobs: Job[],
  manifest: Manifest,
  authState: AuthState,
  authToken: string,
  selectedOrderId?: number
) => {
  AppToasterTopLeft.show({
    intent: Intent.SUCCESS,
    message: (
      <AssignmentToast
        jobs={jobs}
        manifest={manifest}
        authState={authState}
        selectedOrderId={selectedOrderId}
        authToken={authToken}
      />
    ),
    className: "toast",
    timeout: 5000
  });
};

const ReassignmentToastForReassignment = ({jobStops, manifest, authState, authToken}: ReassignmentToastProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const {tenantPreferences} = useContext(PreferenceContext);

  const handleJobClicked = (e: React.MouseEvent<HTMLAnchorElement>, orderId: number) => {
    e.preventDefault();
    setLoading(true);
    navigateToCmsOrder(authState.tenant!, authToken, orderId, tenantPreferences).finally(() => setLoading(false));
  };
  const uniqueJobNumbers = new Set();

  return (
    <div>
      <LoadingSpinner loading={loading}>
        <AssignmentToastContainer>
          <AssignmentHeader>
            <FontAwesomeIcon size={"xl"} color={"#32A467"} icon={solid("circle-check")} />
            <AssignmentTitle>Reassignment Submitted</AssignmentTitle>
          </AssignmentHeader>
          <div>
            Job{jobStops.length > 1 ? "s" : ""}{" "}
            <span>
              {jobStops.map((x, index) => {
                if (!uniqueJobNumbers.has(x.job.jobNumber)) {
                  uniqueJobNumbers.add(x.job.jobNumber);
                  return (
                    <a key={`link-${x.job.jobId}`} href={"./"} onClick={(e) => handleJobClicked(e, x.order.orderId)}>
                      {x.job.jobNumber}
                      {index === jobStops.length - 1 ? " " : ", "}
                    </a>
                  );
                } else {
                  return null;
                }
              })}
            </span>{" "}
            submitted to {manifest.driver.name} and reassignment is processing. We&apos;ll notify you of any issues.
          </div>
        </AssignmentToastContainer>
      </LoadingSpinner>
    </div>
  );
};

type ReassignmentErrorToastProps = {
  manifest: Manifest | undefined;
  onClose: () => void;
  handleOpenManifestDetailWhenClicked: () => void;
};

export const ReassignmentErrorToast: React.FC<ReassignmentErrorToastProps> = ({
  manifest,
  onClose,
  handleOpenManifestDetailWhenClicked
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const navigateToManifest = () => {
    setLoading(true);
    handleOpenManifestDetailWhenClicked();
    setTimeout(() => {
      setLoading(false);
    }, 500);
  };
  return (
    <div>
      <LoadingSpinner loading={loading}>
        <AssignmentToastContainer>
          <AssignmentHeader>
            <FontAwesomeIcon size={"xl"} color={"#FA545E"} icon={solid("triangle-exclamation")} />
            <AssignmentTitle>Reassignment Issue</AssignmentTitle>
          </AssignmentHeader>
          <AssignmentBody>
            Some job(s) failed to reassign. Please check{" "}
            <AssignmentLink onClick={navigateToManifest}>{manifest?.driver.name}</AssignmentLink> manifest, and the
            unassigned job grid, to analyze any further action needed.
          </AssignmentBody>
          <AssignmentFooter>
            <CloseButton onClick={onClose}>Close</CloseButton>
          </AssignmentFooter>
        </AssignmentToastContainer>
      </LoadingSpinner>
    </div>
  );
};

export const showAssignmentToastForReassignment = (
  jobStops: ManifestStop[],
  manifest: Manifest,
  authState: AuthState,
  authToken: string
) => {
  AppToasterTopLeft.show({
    intent: Intent.SUCCESS,
    message: (
      <ReassignmentToastForReassignment
        jobStops={jobStops}
        manifest={manifest}
        authState={authState}
        authToken={authToken}
      />
    ),
    className: "toast",
    timeout: 5000
  });
};

export const showReassignmentErrorToast = (
  manifest: Manifest | undefined,
  manifestDetailsDispatch: Dispatch<ManifestDetailsActions>
) => {
  const toastId = uuidv4();
  const handleToastClose = () => {
    AppToasterTopLeft.dismiss(toastId);
  };
  const handleOpenManifestDetailWhenClicked = () => {
    const manifestDriverId = manifest?.manifestDriverId;
    manifestDetailsDispatch({
      type: "SET_MANIFEST_DETAILS",
      payload: {
        manifestDriverId: manifestDriverId,
        driverId: manifest?.driver.driverId
      }
    });
    setTimeout(() => {
      handleToastClose();
    }, 500);
  };
  AppToasterTopLeft.show(
    {
      intent: Intent.WARNING,
      message: (
        <ReassignmentErrorToast
          manifest={manifest}
          onClose={handleToastClose}
          handleOpenManifestDetailWhenClicked={handleOpenManifestDetailWhenClicked}
        />
      ),
      className: "toast-error",
      timeout: 5000
    },
    toastId
  );
};

export const showSimpleSuccessToast = (title: string, message: string) => {
  AppToasterTopLeft.show({
    message: (
      <AssignmentToastContainer>
        <AssignmentHeader>
          <FontAwesomeIcon size={"xl"} color={"#32A467"} icon={solid("circle-check")} />
          <AssignmentTitle>{title}</AssignmentTitle>
        </AssignmentHeader>
        <div>{message}</div>
      </AssignmentToastContainer>
    ),
    intent: Intent.SUCCESS,
    className: "toast",
    timeout: 50000
  });
};

const JobNumberLink = styled.a`
  color: ${COLORS.LINK_BLUE} !important;
  text-decoration: none;
  cursor: pointer;
  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: underline;
    text-decoration-color: ${COLORS.LINK_BLUE} !important;
    color: ${COLORS.LINK_BLUE} !important;
  }
`;

const ErrorFooterMessage = styled.div`
  margin-top: 12px;
`;

type SplitJobToastProps = {
  jobNumber: string | number;
  driverName: string;
  orderId: string | number;
  authState: AuthState;
  authToken: string;
};

const SplitJobToast = ({jobNumber, driverName, orderId, authState, authToken}: SplitJobToastProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const {tenantPreferences} = useContext(PreferenceContext);

  const handleJobClicked = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      setLoading(true);
      navigateToCmsOrder(authState.tenant!, authToken, orderId, tenantPreferences).finally(() => setLoading(false));
    },
    [authState.tenant, authToken, orderId, tenantPreferences]
  );

  return (
    <div>
      <LoadingSpinner loading={loading}>
        <AssignmentToastContainer>
          <AssignmentHeader>
            <FontAwesomeIcon size={"xl"} color={"#32A467"} icon={solid("circle-check")} />
            <AssignmentTitle>Job Split Submitted</AssignmentTitle>
          </AssignmentHeader>
          <div>
            Part of the job{" "}
            <JobNumberLink href={"./"} onClick={handleJobClicked} data-testid="split-job-success-link">
              {jobNumber}
            </JobNumberLink>{" "}
            submitted to {driverName} and assignment is processing. We&apos;ll notify you of any issues.
          </div>
        </AssignmentToastContainer>
      </LoadingSpinner>
    </div>
  );
};

export const showSplitJobToastSuccess = (
  jobNumber: string | number,
  driverName: string,
  orderId: string | number,
  authState: AuthState,
  authToken: string
) => {
  AppToasterTopLeft.show({
    intent: Intent.SUCCESS,
    message: (
      <SplitJobToast
        jobNumber={jobNumber}
        driverName={driverName}
        orderId={orderId}
        authState={authState}
        authToken={authToken}
      />
    ),
    className: "toast",
    timeout: 5000
  });
};

type SplitJobErrorToastProps = {
  jobNumber: string | number;
  fromDriverName: string;
  toDriverName: string;
  orderId: string | number;
  authState: AuthState;
  authToken: string;
  onClose: () => void;
};

const SplitJobErrorToast = ({
  jobNumber,
  fromDriverName,
  toDriverName,
  orderId,
  authState,
  authToken,
  onClose
}: SplitJobErrorToastProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const {tenantPreferences} = useContext(PreferenceContext);

  const handleJobClicked = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      setLoading(true);
      navigateToCmsOrder(authState.tenant!, authToken, orderId, tenantPreferences).finally(() => setLoading(false));
    },
    [authState.tenant, authToken, orderId, tenantPreferences]
  );

  return (
    <div>
      <LoadingSpinner loading={loading}>
        <AssignmentToastContainer>
          <AssignmentHeader style={{gap: "10px"}}>
            <FontAwesomeIcon size={"xl"} color={"#FA545E"} icon={solid("triangle-exclamation")} />
            <div className="toast-header-content">
              <AssignmentTitle>Split Job Issue</AssignmentTitle>
              <FontAwesomeIcon
                icon={solid("xmark")}
                onClick={onClose}
                className="toast-close-icon"
                data-testid="split-job-error-close-icon"
              />
            </div>
          </AssignmentHeader>
          <AssignmentBody>
            <div>
              Stops from job{" "}
              <JobNumberLink href={"./"} onClick={handleJobClicked} data-testid="split-job-error-link">
                {jobNumber}
              </JobNumberLink>{" "}
              failed to split from {fromDriverName} to {toDriverName}.
            </div>
            <ErrorFooterMessage>Please review their manifests for any additional actions needed.</ErrorFooterMessage>
          </AssignmentBody>
          <AssignmentFooter>
            <CloseButton onClick={onClose}>Close</CloseButton>
          </AssignmentFooter>
        </AssignmentToastContainer>
      </LoadingSpinner>
    </div>
  );
};

export const showSplitJobErrorToast = (
  jobNumber: string | number,
  fromDriverName: string,
  toDriverName: string,
  orderId: string | number,
  authState: AuthState,
  authToken: string
) => {
  const toastId = uuidv4();
  const handleToastClose = () => {
    AppToasterTopLeft.dismiss(toastId);
  };
  AppToasterTopLeft.show(
    {
      intent: Intent.WARNING,
      message: (
        <SplitJobErrorToast
          jobNumber={jobNumber}
          fromDriverName={fromDriverName}
          toDriverName={toDriverName}
          orderId={orderId}
          authState={authState}
          authToken={authToken}
          onClose={handleToastClose}
        />
      ),
      className: "split-job-error-toast",
      isCloseButtonShown: false,
      timeout: 99000
    },
    toastId
  );
};
